/**
 * jffd-bundle-cart.js
 *
 * Inserts a bundle card row before each group of cart line items that share a
 * `data-bundle-id` attribute. The card shows the parent product image, title,
 * component list (from per-row data-bundle-item-title/qty attrs), total price,
 * an "Edit bundle" link, and a "Remove bundle" button.
 *
 * Architecture: N cart line items per bundle — one per recipe
 * variant selected. Each row carries data-bundle-id, data-bundle-item-title,
 * and data-bundle-item-qty stamped server-side by cart-products.liquid from
 * the _bundle_* line item properties set during add-to-cart.
 */

const BUNDLE_ACTIONS_CLASS = 'jffd-bundle-actions-row';
const CART_UPDATE_EVENT = 'cart:update';

// Dedup flag — prevents concurrent initBundleRows executions.
let initPending = false;

function scheduleInit() {
  if (initPending) return;
  initPending = true;
  requestAnimationFrame(() => {
    initPending = false;
    initBundleRows();
  });
}

/**
 * Formats a price in cents to a localised currency string.
 * Uses Shopify.currency.active when available, falls back to shop money format.
 *
 * @param {number} cents
 * @returns {string}
 */
function formatPrice(cents) {
  const currency =
    (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch (_) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

/**
 * Computes the sum of child recipe line prices and injects it into the
 * parent row's price cell, replacing the $0 bundle variant price.
 *
 * @param {HTMLTableRowElement[]} rows - All rows sharing a bundle ID.
 */
function injectBundleTotal(rows) {
  const parentRow = rows.find((r) => r.dataset.bundleIsParent === 'true');
  if (!parentRow) return;

  const totalCents = rows
    .filter((r) => r.dataset.bundleIsParent !== 'true')
    .reduce((sum, r) => sum + (parseInt(r.dataset.bundleLinePrice, 10) || 0), 0);

  const priceCell = parentRow.querySelector('.cart-items__price');
  if (!priceCell) return;

  const formatted = formatPrice(totalCents);
  const textComp = priceCell.querySelector('text-component');
  if (textComp) {
    textComp.setAttribute('value', formatted);
    textComp.textContent = formatted;
  } else {
    priceCell.textContent = formatted;
  }
}

/**
 * inserts a bundle card row before the first row in each group, and wires
 * up "Remove bundle" and "Edit bundle" actions.
 *
 * Idempotent — already-processed groups are skipped.
 */
function initBundleRows() {
  /** @type {Map<string, HTMLTableRowElement[]>} */
  const groups = new Map();

  document
    .querySelectorAll('tr[data-bundle-id]:not([data-bundle-id=""])')
    .forEach((row) => {
      const id = row.dataset.bundleId;
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(/** @type {HTMLTableRowElement} */ (row));
    });

  groups.forEach((rows, bundleId) => {
    const firstRow = rows[0];

    // Skip if a card row for this bundle already exists directly before the first item row.
    const prev = firstRow.previousElementSibling;
    if (prev?.classList.contains(BUNDLE_ACTIONS_CLASS) && prev.dataset.bundleId === bundleId) return;

    // For new nested bundles the parent size variant is a real cart line —
    // Shopify renders it natively with children indented below. Skip card injection.
    const hasNativeParent = rows.some((r) => r.dataset.bundleIsParent === 'true');
    if (hasNativeParent) {
      injectBundleTotal(rows);
      return;
    }

    const {
      bundleUrl = '',
      bundleProductTitle = '',
      bundleProductImageUrl = '',
      bundleVariantName = '',
      bundleSellingPlanName = '',
    } = firstRow.dataset;

    // Sum total price across all recipe rows (values are raw Shopify cents).
    const totalCents = rows.reduce(
      (sum, r) => sum + (parseInt(r.dataset.bundleLinePrice, 10) || 0),
      0
    );

    // Build component list from per-row data attributes (Pattern A).
    // Each recipe line item carries data-bundle-item-title and data-bundle-item-qty.
    /** @type {{ title: string; qty: number }[]} */
    const components = rows
      .map((r) => ({ title: r.dataset.bundleItemTitle || '', qty: parseInt(r.dataset.bundleItemQty, 10) || 0 }))
      .filter((c) => c.qty > 0);

    // ── Card row ────────────────────────────────────────────────────────────
    const cardRow = document.createElement('tr');
    cardRow.className = BUNDLE_ACTIONS_CLASS;
    cardRow.setAttribute('data-bundle-id', bundleId);

    const cell = document.createElement('td');
    cell.colSpan = 999;

    const card = document.createElement('div');
    card.className = 'jffd-bundle-card';
    if (firstRow.dataset.bundleEditInheritColorScheme !== 'true' && firstRow.dataset.bundleEditColorScheme) {
      card.classList.add(`color-${firstRow.dataset.bundleEditColorScheme}`);
    }

    // Image
    if (bundleProductImageUrl) {
      const imageWrap = document.createElement('div');
      imageWrap.className = 'jffd-bundle-card__image';
      const img = document.createElement('img');
      img.src = bundleProductImageUrl;
      img.alt = bundleProductTitle;
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;
      imageWrap.appendChild(img);
      card.appendChild(imageWrap);
    }

    // Details column
    const details = document.createElement('div');
    details.className = 'jffd-bundle-card__details';

    const title = document.createElement('p');
    title.className = 'jffd-bundle-card__title';
    title.textContent = bundleVariantName
      ? `${bundleProductTitle} — ${bundleVariantName}`
      : bundleProductTitle;
    details.appendChild(title);

    if (components.length) {
      const list = document.createElement('ul');
      list.className = 'jffd-bundle-card__components';
      components.forEach(({ title: itemTitle, qty }) => {
        const li = document.createElement('li');
        li.textContent = qty > 1 ? `${itemTitle} × ${qty}` : itemTitle;
        list.appendChild(li);
      });
      details.appendChild(list);
    }

    if (bundleSellingPlanName) {
      const plan = document.createElement('p');
      plan.className = 'jffd-bundle-card__plan';
      plan.textContent = bundleSellingPlanName;
      details.appendChild(plan);
    }

    if (bundleUrl) {
      const editLink = document.createElement('a');
      editLink.className = 'jffd-bundle-card__edit';
      editLink.href = `${bundleUrl}?edit_bundle_id=${encodeURIComponent(bundleId)}`;
      editLink.textContent = firstRow.dataset.bundleEditLabel || 'Edit bundle';
      editLink.classList.add(firstRow.dataset.bundleEditStyle || 'link');
      if (firstRow.dataset.bundleEditInheritColorScheme !== 'true' && firstRow.dataset.bundleEditColorScheme) {
        editLink.classList.add(`color-${firstRow.dataset.bundleEditColorScheme}`);
      }
      if (firstRow.dataset.bundleEditCustomPadding === 'true') {
        editLink.classList.add('bundle-edit-button--custom-padding');
      }
      if (firstRow.dataset.bundleEditCustomPaddingMobile === 'true') {
        editLink.classList.add('bundle-edit-button--custom-padding-mobile');
      }
      editLink.style.setProperty('--bundle-edit-font-size', firstRow.dataset.bundleEditFontSize || '');
      editLink.style.setProperty('--bundle-edit-width', firstRow.dataset.bundleEditWidth || 'fit-content');
      editLink.style.setProperty('--bundle-edit-alignment', firstRow.dataset.bundleEditAlignment || 'flex-start');
      if (firstRow.dataset.bundleEditCustomPadding === 'true') {
        editLink.style.setProperty('--bundle-edit-padding-block-start', `${firstRow.dataset.bundleEditPaddingBlockStart || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-block-end', `${firstRow.dataset.bundleEditPaddingBlockEnd || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-inline-start', `${firstRow.dataset.bundleEditPaddingInlineStart || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-inline-end', `${firstRow.dataset.bundleEditPaddingInlineEnd || 0}px`);
      }
      if (firstRow.dataset.bundleEditCustomPaddingMobile === 'true') {
        editLink.style.setProperty('--bundle-edit-padding-block-start-mobile', `${firstRow.dataset.bundleEditPaddingBlockStartMobile || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-block-end-mobile', `${firstRow.dataset.bundleEditPaddingBlockEndMobile || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-inline-start-mobile', `${firstRow.dataset.bundleEditPaddingInlineStartMobile || 0}px`);
        editLink.style.setProperty('--bundle-edit-padding-inline-end-mobile', `${firstRow.dataset.bundleEditPaddingInlineEndMobile || 0}px`);
      }
      details.appendChild(editLink);
    }

    card.appendChild(details);

    // Actions column (price + remove)
    const actions = document.createElement('div');
    actions.className = 'jffd-bundle-card__actions';

    const priceEl = document.createElement('span');
    priceEl.className = 'jffd-bundle-card__price';
    priceEl.textContent = formatPrice(totalCents);
    actions.appendChild(priceEl);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'jffd-bundle-card__remove';
    removeBtn.textContent = 'Remove bundle';
    // Capture line keys now; DOM refs may be stale after cart re-render.
    const lineKeys = rows.map((r) => r.dataset.key).filter(Boolean);
    removeBtn.addEventListener('click', () => removeBundle(lineKeys, bundleId));
    actions.appendChild(removeBtn);

    card.appendChild(actions);
    cell.appendChild(card);
    cardRow.appendChild(cell);
    firstRow.before(cardRow);
  });
}

/**
 * Removes all bundle line items via sequential `/cart/change.js` calls.
 * Using per-key change calls (not /cart/update.js) avoids zeroing the same
 * recipe variant in a different bundle that happens to share the same variant ID.
 * Sections are requested only on the final call to trigger a single re-render.
 *
 * @param {string[]} lineKeys - Shopify cart line item keys for all recipe rows.
 * @param {string}   bundleId - Bundle ID (used only for logging).
 */
async function removeBundle(lineKeys, bundleId) {
  if (!lineKeys?.length) return;

  // Collect section IDs so Horizon can morph both cart page and drawer in one
  // round-trip. If none are found we fall back to a cache-busting re-render.
  const sectionIds = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
        (el) => /** @type {HTMLElement} */ (el).dataset.sectionId
      )
    ),
  ].filter(Boolean);

  try {
    let lastData = null;
    for (let i = 0; i < lineKeys.length; i++) {
      const isLast = i === lineKeys.length - 1;
      const body = JSON.stringify({
        id: lineKeys[i],
        quantity: 0,
        ...(isLast && sectionIds.length && {
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      });

      if (!response.ok) return;
      lastData = await response.json();
    }

    if (!lastData) return;

    // Compute logical count: subtract bundle child quantities so each bundle = 1
    const bundleChildQty = (lastData.items || []).reduce((sum, item) => {
      const props = item.properties || {};
      return props._bundle_id && props._bundle_is_parent !== 'true'
        ? sum + (item.quantity || 0)
        : sum;
    }, 0);
    const logicalCount = (lastData.item_count || 0) - bundleChildQty;

    // Dispatch cart:update with the shape CartItemsComponent's #handleCartUpdate
    // expects so morphSection is called with fresh HTML (no extra fetch needed).
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE_EVENT, {
        bubbles: true,
        detail: {
          resource: lastData,
          sourceId: 'jffd-bundle-cart',
          data: {
            source: 'jffd-bundle-cart',
            itemCount: logicalCount,
            sections: lastData.sections ?? {},
          },
        },
      })
    );
  } catch (err) {
    console.error('[jffd-bundle-cart] removeBundle failed', err);
  }
}

// ── Initialisation ──────────────────────────────────────────────────────────

// Run once after the current script has settled.
scheduleInit();

// Re-run after every cart update so action rows survive cart drawer re-morphs.
// scheduleInit is debounced so rapid successive events collapse into one rAF.
document.addEventListener(CART_UPDATE_EVENT, scheduleInit);

// Also pick up the custom event fired by the bundle ATC immediately after add.
document.addEventListener('jffd:bundle:added', scheduleInit);
